import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, CircularProgress } from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptIcon from '@mui/icons-material/Receipt';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import SupportIcon from '@mui/icons-material/Support';
import SettingsIcon from '@mui/icons-material/Settings';
import LanguageIcon from '@mui/icons-material/Language';
import LogoutIcon from '@mui/icons-material/Logout';
import ProfileView from './ProfileView';
import SettingsView from './SettingsView';
import CustomerSupportView from './CustomerSupportView';
import Tutorial from './Tutorial';
import Pricing from './Pricing';
import Products from './Products';
import PaymentHistoryView from './PaymentHistoryView';
import ProjectHistoryView from './ProjectHistoryView';
import { AppShell, LoadingState, useToast } from '../../ui';

const NAV = [
  { label: 'Products', value: 'products', icon: <Inventory2Icon fontSize="small" /> },
  { label: 'Tutorials', value: 'tutorial', icon: <OndemandVideoIcon fontSize="small" /> },
  { label: 'Profile', value: 'profile', icon: <PersonIcon fontSize="small" /> },
  { label: 'Payment history', value: 'paymentHistory', icon: <ReceiptIcon fontSize="small" /> },
  { label: 'Project history', value: 'projectHistory', icon: <WorkHistoryIcon fontSize="small" /> },
  { label: 'Customer support', value: 'support', icon: <SupportIcon fontSize="small" /> },
];

const getToken = () => localStorage.getItem('jwttoken') || sessionStorage.getItem('jwttoken');

/** "Request a trial" button at the bottom of the sidebar. */
const RequestTrialButton = () => {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const handleRequestTrial = async () => {
    const jwt = getToken();
    if (!jwt) {
      toast('Please log in again before requesting a trial.', 'error');
      return;
    }
    setBusy(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/request-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      });
      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (parseError) {
        data = { message: text };
      }
      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit trial request.');
      }
      toast('Trial request sent. We will be in touch soon.');
    } catch (error) {
      toast(error.message || 'Unable to submit trial request. Please try again.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button variant="contained" fullWidth onClick={handleRequestTrial} disabled={busy}>
      {busy ? <CircularProgress size={20} color="inherit" /> : 'Request a trial'}
    </Button>
  );
};

const Dashboard = () => {
  const [selectedView, setSelectedView] = useState('products');
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const successMessage = location.state?.successMessage;

  useEffect(() => {
    if (error) {
      localStorage.removeItem('jwttoken');
      sessionStorage.removeItem('jwttoken');
      navigate('/login?as=user', { replace: true });
    }
  }, [error, navigate]);

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/userdata/profile`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const json = await response.json();
        if (json.success) {
          setUserData(json);
        } else {
          setError(json.message || 'Failed to fetch user data');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('jwttoken');
    sessionStorage.removeItem('jwttoken');
    navigate('/login?as=user');
  };

  const renderContent = () => {
    switch (selectedView) {
      case 'profile':
        return <ProfileView userData={userData} />;
      case 'paymentHistory':
        return <PaymentHistoryView />;
      case 'projectHistory':
        return <ProjectHistoryView />;
      case 'settings':
        return <SettingsView />;
      case 'support':
        return <CustomerSupportView />;
      case 'tutorial':
        return <Tutorial />;
      case 'pricing':
        return <Pricing />;
      case 'products':
      default:
        return <Products />;
    }
  };

  if (loading) {
    return <LoadingState fullPage />;
  }

  if (error || !userData) {
    return null;
  }

  return (
    <AppShell
      area="Dashboard"
      homeTo="/dashboard"
      nav={NAV}
      selected={selectedView}
      onSelect={setSelectedView}
      sidebarFooter={<RequestTrialButton />}
      account={{
        name: userData.displayName,
        email: userData.email || localStorage.getItem('email') || sessionStorage.getItem('email') || undefined,
        items: [
          { label: 'Profile', onClick: () => setSelectedView('profile'), icon: <PersonIcon fontSize="small" /> },
          { label: 'Account settings', onClick: () => setSelectedView('settings'), icon: <SettingsIcon fontSize="small" /> },
          { label: 'Vavetek website', onClick: () => window.open('https://vavetek.ai/', '_blank', 'noopener'), icon: <LanguageIcon fontSize="small" /> },
          { label: 'Log out', onClick: handleLogout, icon: <LogoutIcon fontSize="small" /> },
        ],
      }}
    >
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}
      {renderContent()}
    </AppShell>
  );
};

export default Dashboard;
